/* eslint-disable prettier/prettier */
import React from 'react';
import { useParams } from 'react-router';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalFooterButtons,
  ModalFooterCancelButton,
  ModalFooterPrimaryButton,
  ModalHeader,
  ModalCustomContent,
} from '../../../components/modal/Modal';

import { getDescription } from '../OptionDetails';
import { useTranslate } from '../../../../core/translate';
import ModalImage from '../../../components/modal/ModalImage';
import LegalDisclosure from '../../../components/legalDisclosure/LegalDisclosure';
import { useSearchParams } from 'react-router-dom';

const SimpleOptionDetailsInner = ({ option }) => {
  const { largeImageUrl, description: header, disclaimer, label } = option;
  const description = getDescription(option);

  return (
    <>
      <LegalDisclosure
        className="legal-disclosure--simple-option-details"
        showAsStatic
      />
      <div className="simple-option-details__container">
        <div className="simple-option-details">
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            className="simple-option-details__description"
          />
          <div
            dangerouslySetInnerHTML={{ __html: disclaimer }}
            className="simple-option-details__disclaimer"
          />
        </div>
        <ModalImage
          url={largeImageUrl}
          alt={header || label}
          showImageDisclosure
        />
      </div>
    </>
  );
};

const SimpleOptionDetailsInnerWithButtons = ({
  option,
  setIsClosing,
  onCloseModal,
  onSelect,
  onDeselect,
  showControls,
}) => {
  const {
    largeImageUrl,
    description: header,
    disclaimer,
    label,
    primaryButtonLabel,
    secondaryButtonLabel,
  } = option;
  const description = getDescription(option);

  return (
    <>
      <div className="simple-option-details__container">
        <div className="simple-option-details">
          <div
            dangerouslySetInnerHTML={{ __html: header }}
            className="simple-option-details__header"
          />
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            className="simple-option-details__description"
          />
          {disclaimer && (
            <div
              dangerouslySetInnerHTML={{ __html: disclaimer }}
              className="simple-option-details__disclaimer"
            />
          )}
          {(primaryButtonLabel || secondaryButtonLabel) && (
            <div className="simple-option-details__buttons-container">
              {(primaryButtonLabel && showControls) && (
                <button
                  className="simple-option-details__selection-button stat-button-link"
                  onClick={() => setIsClosing(true, onCloseModal(onSelect))}
                  data-dtm={header}>
                  {primaryButtonLabel}
                </button>
              )}
              {(secondaryButtonLabel && showControls) && (
                <button
                  className="simple-option-details__cancel-button stat-button-link"
                  onClick={() => setIsClosing(true, onCloseModal(onDeselect))}
                  data-dtm={header}>
                  {secondaryButtonLabel}
                </button>
              )}
            </div>
          )}
        </div>
        <ModalImage
          url={largeImageUrl}
          alt={header || label}
          showImageDisclosure
        />
      </div>
    </>
  );
};

const SimpleOptionDetailsAlt = ({ option, onClose, onSelect, onDeselect, showControls }) => {
  const header = option?.description || option?.label;

  return (
    <Modal isOpen={true} contentLabel={option?.code} onRequestClose={onClose}>
      <ModalHeader onClose={onClose} analyticsKey={header} />
      <ModalCustomContent>
        {({ onCloseModal, setIsClosing }) => (
          <div className="modal-body">
            <div className="modal-body__inner modal-body__inner--alt">
              <SimpleOptionDetailsInnerWithButtons
                option={option}
                setIsClosing={setIsClosing}
                onCloseModal={onCloseModal}
                onSelect={onSelect}
                onDeselect={onDeselect}
                showControls={showControls}
              />
            </div>
          </div>
        )}
      </ModalCustomContent>
    </Modal>
  );
};

const SimpleOptionDetails = ({
  options,
  onClose,
  onSelect,
  isEngine = null,
  onlyCloseButton,
}) => {
  const [searchParams] = useSearchParams();
  const infoOnly = searchParams.get('infoOnly');
  const { id } = useParams();
  const option = options?.find((option) =>
    isEngine ? option.id === id : option.code === id
  );
  const header = option?.description || option?.label;

  const translate = useTranslate();

  const FooterButtons = ({ setIsClosing, onCloseModal }) => {
    if (infoOnly === 'true') {
      return (
        <ModalFooterPrimaryButton
          className="option-details__footer-button stat-button-link"
          onSelect={() => setIsClosing(true, onCloseModal(onClose))}
          data-dtm={header}>
          {translate('byo.page.general.ok')}
        </ModalFooterPrimaryButton>
      );
    }

    if (onlyCloseButton) {
      return (
        <ModalFooterPrimaryButton
          className="option-details__footer-button stat-button-link"
          onSelect={() => setIsClosing(true, onCloseModal(onClose))}
          data-dtm={header}>
          {translate('byo.page.general.close')}
        </ModalFooterPrimaryButton>
      );
    }

    return (
      <ModalFooterButtons classModifier="simple-option-details">
        <ModalFooterCancelButton
          className="option-details__footer-button stat-button-link"
          onClose={() => setIsClosing(true, onCloseModal(onClose))}
          data-dtm={header}
        />
        <ModalFooterPrimaryButton
          className="option-details__footer-button stat-button-link"
          onSelect={() =>
            setIsClosing(
              true,
              onCloseModal().then(() => {
                onSelect(option);
              })
            )
          }
          data-dtm={header}>
          {translate('byo.page.general.select')}
        </ModalFooterPrimaryButton>
      </ModalFooterButtons>
    );
  };

  return (
    <Modal isOpen={true} contentLabel={option?.code} onRequestClose={onClose}>
      <ModalHeader text={header} onClose={onClose} analyticsKey={header} />
      <ModalBody>
        <SimpleOptionDetailsInner option={option} />
      </ModalBody>
      <ModalFooter>
        <ModalCustomContent>
          {({ onCloseModal, setIsClosing }) => (
            <FooterButtons
              setIsClosing={setIsClosing}
              onCloseModal={onCloseModal}
            />
          )}
        </ModalCustomContent>
      </ModalFooter>
    </Modal>
  );
};

export { SimpleOptionDetailsInner, SimpleOptionDetailsAlt };

export default SimpleOptionDetails;
